import { dateKey } from "./calendar-dates";
import { mergePublicPresence } from "./city-day-presentation";
import type { PublicMapContext } from "./city-day-map";

/** Compose only the API's public stays and existing selectable slot controls. */
export function mountDaySchedule(
  panel: HTMLElement,
  context: PublicMapContext | undefined,
  view: {
    date: string;
    timeZone: string;
    locale: string;
    selectedPlaceId: string;
  },
): void {
  if (!context || !view.date) return;
  const active = panel.ownerDocument.activeElement;
  const focusedSlot =
    active instanceof HTMLButtonElement && active.classList.contains("slot")
      ? active
      : null;
  const buttons = [...panel.querySelectorAll<HTMLButtonElement>(".slot")];
  const items = mergePublicPresence(
    context.timeline ?? [],
    context.stayMergeGapMinutes ?? 5,
  ).filter(
    (item) =>
      dateKey(new Date(item.startsAt), view.timeZone) <= view.date &&
      dateKey(new Date(Date.parse(item.endsAt) - 1), view.timeZone) >=
        view.date,
  );
  const key = JSON.stringify([
    items,
    context.timelineState,
    view,
    buttons.map((button) => button.outerHTML),
  ]);
  const existing = panel.querySelector<HTMLOListElement>(".day-timeline");
  if (existing?.dataset.scheduleKey === key) return;
  const document = panel.ownerDocument;
  const list = document.createElement("ol");
  list.className = "day-timeline";
  list.dataset.scheduleKey = key;
  list.setAttribute(
    "aria-label",
    view.locale === "cs"
      ? "Program dne a volné termíny"
      : "Day schedule and available times",
  );
  const time = new Intl.DateTimeFormat(
    view.locale === "cs" ? "cs-CZ" : "en-GB",
    {
      timeZone: view.timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    },
  );
  const entries = [
    ...items.map((item) => ({ start: item.startsAt, item })),
    ...buttons.map((button) => ({ start: button.dataset.start ?? "", button })),
  ].sort(
    (a, b) =>
      Date.parse(a.start) - Date.parse(b.start) || ("item" in a ? -1 : 1),
  );
  let slotGroup: HTMLDivElement | undefined;
  for (const entry of entries) {
    if ("button" in entry) {
      if (!slotGroup) {
        const row = document.createElement("li");
        row.className = "day-timeline-available";
        slotGroup = document.createElement("div");
        slotGroup.className = "slot-list";
        row.append(slotGroup);
        list.append(row);
      }
      slotGroup.append(entry.button);
      continue;
    }
    slotGroup = undefined;
    const item = entry.item;
    const row = document.createElement("li");
    row.className = `day-timeline-${item.kind}`;
    row.dataset.presenceId = item.id;
    if (item.placeId) row.dataset.presencePlace = item.placeId;
    const interval = document.createElement("span");
    interval.className = "day-timeline-period";
    interval.textContent = `${time.format(new Date(item.startsAt))}–${time.format(new Date(item.endsAt))}`;
    const details = document.createElement("div");
    const label = document.createElement("strong");
    label.textContent =
      item.kind === "travel"
        ? view.locale === "cs"
          ? "Plánovaný přesun"
          : "Planned travel"
        : item.precision === "exact"
          ? item.label
          : `${view.locale === "cs" ? "Jiné místo" : "Other place"}${item.label ? ` · ${item.label}` : ""}`;
    details.append(label);
    if (item.kind === "stay" && item.precision === "exact" && item.address) {
      const address = document.createElement("small");
      address.textContent = item.address;
      details.append(address);
    }
    const state = document.createElement("small");
    state.textContent =
      context.timelineState === "stale"
        ? view.locale === "cs"
          ? "Dříve ověřený pobyt"
          : "Previously checked stay"
        : item.kind === "travel"
          ? view.locale === "cs"
            ? "Informace o přesunu"
            : "Travel information"
          : view.locale === "cs"
            ? "Již naplánováno"
            : "Already scheduled";
    details.append(state);
    row.append(interval, details);
    list.append(row);
  }
  // Existing buttons are moved rather than recreated: their handlers, disabled
  // state, server recommendations and focus remain authoritative.
  if (existing) existing.replaceWith(list);
  else {
    const slots = panel.querySelector(".slot-list");
    if (slots) slots.replaceWith(list);
    else panel.querySelector(".day-slots-heading, h3")?.after(list);
  }
  panel.querySelectorAll(".slot-list:empty").forEach((node) => node.remove());
  if (focusedSlot?.isConnected) focusedSlot.focus({ preventScroll: true });
}
